import React from "react";
import { cn } from "@/lib/utils";

/**
 * The document head shared by the work surfaces: title and state word on the left, the mode switch and the one highlighted
 * action on the right. It stays at the top while the surface scrolls; once you are reading, the second line (subtitle,
 * key figures, the one-sentence note) folds away and the head gets quiet. `toolbar` is the row that never folds (search, filters).
 */
export function StickyHead({ headRef, compact, title, aside, subtitle, controls, keyline, note, toolbar, maxWidth, className, testId, titleTestId, noteTestId }) {
  return (
    <header ref={headRef} className={cn("sticky top-0 z-20 border-b backdrop-blur-md transition-colors duration-150", compact ? "border-black/[0.06]" : "border-transparent", className)} data-testid={testId} data-compact={compact ? "1" : "0"}>
      <div className={cn("mx-auto w-full px-5 transition-[padding] duration-150 sm:px-8", compact ? "py-2.5" : "pb-3.5 pt-5")} style={{ maxWidth }}>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2.5">
          <div className="min-w-0 flex-1">
            <div className="flex min-w-0 items-center gap-2">
              <h2 className={cn("truncate font-semibold tracking-[-0.02em] transition-[font-size] duration-150", compact ? "text-[17px]" : "text-[22px]")} data-testid={titleTestId || `${testId}-title`}>{title}</h2>
              {aside}
            </div>
            {subtitle ? <div className="tilbud-collapse" data-open={compact ? "0" : "1"}><div><p className="mt-0.5 truncate text-[14px] text-muted-foreground" data-testid={`${testId}-subtitle`}>{subtitle}</p></div></div> : null}
          </div>
          <div className="flex flex-wrap items-center gap-2 max-sm:w-full" data-testid={`${testId}-controls`}>{controls}</div>
        </div>
        {keyline || note ? (
          <div className="tilbud-collapse" data-open={compact ? "0" : "1"}>
            <div>
              {keyline ? <div className="mt-3">{keyline}</div> : null}
              {note ? <p className="mt-2.5 rounded-[12px] bg-white px-3.5 py-2.5 text-[14px] leading-snug text-foreground/80 shadow-[0_0_0_1px_hsl(var(--shadow-color)/0.06)]" data-testid={noteTestId || `${testId}-note`}>{note}</p> : null}
            </div>
          </div>
        ) : null}
        {toolbar ? <div className={cn("transition-[margin] duration-150", compact ? "mt-2" : "mt-3.5")}>{toolbar}</div> : null}
      </div>
    </header>
  );
}

/** «a · b · c» – facts that can be scanned; the separator is drawn by CSS so a wrapped line never ends with a dangling dot. */
export function KeylineRow({ children, testId }) {
  return <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1 text-[13.5px] text-muted-foreground [&>span+span]:before:mr-2 [&>span+span]:before:text-muted-foreground/40 [&>span+span]:before:content-['·']" data-testid={testId}>{children}</div>;
}
